import { FC, useContext, useMemo } from 'react';

import {
  ComputerIcon,
  ConsumptionIcon,
  IncomeIcon,
  SafeIcon,
  WalletArrowLeftIcon,
} from '@smartshell/ui-kit';

import { ReportSummaryBlock, ReportSummaryTooltip } from 'components';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { formatMoney, useSummaryValues } from '../utils';
import s from './FinancialSummary.module.scss';
import { IFinancialSummaryProps } from './interfaces';

export const FinancialSummary: FC<IFinancialSummaryProps> = ({
  data,
  loading,
}) => {
  const { t } = useContext(LocalizationContext);
  const { currency } = useContext(SettingsContext).state.settings;

  const { getValue, depositFillPercent } = useSummaryValues(data, loading);

  const normalizeData = useMemo(() => {
    return [
      {
        text: t('money.proceeds'),
        value: `${formatMoney(getValue('total_revenue'), currency.sign)}`,
        icon: <WalletArrowLeftIcon color="secondary" />,
      },
      {
        text: t('visitors.finance.proceeds'),
        value: `${formatMoney(
          getValue('avg_revenue_per_host'),
          currency.sign
        )}`,
        icon: <ComputerIcon color="secondary" />,
        tooltip: (
          <ReportSummaryTooltip
            title={t('visitors.finance.proceeds')}
            footer={t('visitors.finance.proceeds.tooltip')}
            variant="secondary"
          />
        ),
      },
      {
        text: t('visitors.finance.parish.orders'),
        value: `${formatMoney(getValue('pko_amount'), currency.sign)}`,
        icon: <IncomeIcon color="secondary" />,
      },
      {
        text: t('visitors.finance.consumable.orders'),
        value: `${formatMoney(getValue('rko_amount'), currency.sign)}`,
        icon: <ConsumptionIcon color="secondary" />,
      },
      {
        text: t('deposit.fill.title'),
        value: `${formatMoney(getValue('deposit_revenue'), currency.sign)}`,
        icon: <SafeIcon color="secondary" />,
        percent: !loading ? `${depositFillPercent}%` : '-',
        tooltip: (
          <ReportSummaryTooltip
            title={t('deposit.fill.title')}
            text_1={t('renewal.sum')}
            value_1={`${formatMoney(
              getValue('deposit_revenue'),
              currency.sign
            )}`}
            text_2={t('percentage.total.revenue')}
            value_2={!loading ? `${depositFillPercent}%` : '-'}
            footer={t('share.replenishment.deposit')}
            variant="primary"
          />
        ),
      },
    ];
  }, [t, getValue, depositFillPercent, currency.sign]);

  return (
    <div className={s.container}>
      {normalizeData.map((item) => (
        <ReportSummaryBlock
          className={s.summary}
          key={`${item.text}-${item.value}`}
          item={item}
        />
      ))}
    </div>
  );
};
